import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  doc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ClientRow {
  id: string;
  name: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  createdAt: any;
}

interface ClientForm {
  name: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

const BLANK_FORM: ClientForm = {
  name: '',
  address: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

export function FirmClients() {
  const { firmAdmin } = useAuth();
  const { firmId } = firmAdmin!;

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [enrolledCounts, setEnrolledCounts] = useState<Record<string, { total: number; approved: number; pending: number }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<ClientForm>(BLANK_FORM);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ClientForm>(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => { loadData(); }, [firmId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [clientsSnap, profilesSnap] = await Promise.all([
        getDocs(query(collection(db, 'clients'), where('firmId', '==', firmId))),
        getDocs(query(collection(db, 'profiles'), where('firmId', '==', firmId))),
      ]);

      const counts: Record<string, { total: number; approved: number; pending: number }> = {};
      for (const d of profilesSnap.docs) {
        const p = d.data();
        const cid = p.clientId;
        if (!cid) continue;
        if (!counts[cid]) counts[cid] = { total: 0, approved: 0, pending: 0 };
        counts[cid].total++;
        if (p.enrollmentStatus === 'approved') counts[cid].approved++;
        if (p.enrollmentStatus === 'pending') counts[cid].pending++;
      }
      setEnrolledCounts(counts);

      setClients(
        clientsSnap.docs
          .map(d => ({
            id: d.id,
            name: d.data().name ?? '',
            address: d.data().address ?? '',
            contactName: d.data().contactName ?? '',
            contactEmail: d.data().contactEmail ?? '',
            contactPhone: d.data().contactPhone ?? '',
            createdAt: d.data().createdAt,
          }))
          .sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load clients.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = addForm.name.trim();
    if (!name) return;
    setAdding(true);
    setAddError('');
    try {
      await addDoc(collection(db, 'clients'), {
        firmId,
        name,
        address: addForm.address.trim() || null,
        contactName: addForm.contactName.trim() || null,
        contactEmail: addForm.contactEmail.trim().toLowerCase() || null,
        contactPhone: addForm.contactPhone.trim() || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setAddForm(BLANK_FORM);
      setAddOpen(false);
      await loadData();
    } catch (e: any) {
      setAddError(e.message || 'Failed to add client.');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (client: ClientRow) => {
    setEditingId(client.id);
    setEditForm({
      name: client.name,
      address: client.address,
      contactName: client.contactName,
      contactEmail: client.contactEmail,
      contactPhone: client.contactPhone,
    });
    setEditError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    const name = editForm.name.trim();
    if (!name) return;
    setSaving(true);
    setEditError('');
    try {
      await updateDoc(doc(db, 'clients', editingId), {
        name,
        address: editForm.address.trim() || null,
        contactName: editForm.contactName.trim() || null,
        contactEmail: editForm.contactEmail.trim().toLowerCase() || null,
        contactPhone: editForm.contactPhone.trim() || null,
        updatedAt: serverTimestamp(),
      });
      setEditingId(null);
      await loadData();
    } catch (e: any) {
      setEditError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1000 }}>
        <div className="page-header">
          <h1 className="page-title">Clients</h1>
          <button
            className="btn-primary"
            onClick={() => { setAddOpen(o => !o); setAddError(''); setAddForm(BLANK_FORM); }}
            style={{ padding: '8px 18px', fontSize: 13 }}
          >
            {addOpen ? 'Cancel' : '+ Add Client'}
          </button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Add form */}
        {addOpen && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Add Client
            </div>
            {addError && <div className="error-message" style={{ marginBottom: 14 }}>{addError}</div>}
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-input"
                    required
                    value={addForm.name}
                    onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. TechCorp Layoffs"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    className="form-input"
                    value={addForm.address}
                    onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="123 Main St, City, ST 00000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input
                    className="form-input"
                    value={addForm.contactName}
                    onChange={e => setAddForm(f => ({ ...f, contactName: e.target.value }))}
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input
                    className="form-input"
                    type="email"
                    value={addForm.contactEmail}
                    onChange={e => setAddForm(f => ({ ...f, contactEmail: e.target.value }))}
                    placeholder="jane@techcorp.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    className="form-input"
                    value={addForm.contactPhone}
                    onChange={e => setAddForm(f => ({ ...f, contactPhone: e.target.value }))}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn-primary" disabled={adding}>
                  {adding ? 'Adding…' : 'Add Client'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setAddOpen(false); setAddError(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading">Loading…</div>
          ) : clients.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
              No clients added yet.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Approved</th>
                    <th style={{ textAlign: 'right' }}>Pending</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => (
                    <React.Fragment key={c.id}>
                      <tr>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td>
                          {c.contactName && (
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{c.contactName}</div>
                          )}
                          {c.contactEmail && (
                            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                              {c.contactEmail}
                            </div>
                          )}
                          {c.contactPhone && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {c.contactPhone}
                            </div>
                          )}
                          {!c.contactName && !c.contactEmail && !c.contactPhone && (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {c.address || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 500 }}>
                          {enrolledCounts[c.id]?.total ?? 0}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--accent-text)' }}>
                          {enrolledCounts[c.id]?.approved ?? 0}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--warning)' }}>
                          {enrolledCounts[c.id]?.pending ?? 0}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {fmtDate(c.createdAt)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Link
                              to={`/firm/clients/${c.id}/report`}
                              className="btn-secondary"
                              style={{ padding: '4px 12px', fontSize: 12, textDecoration: 'none' }}
                            >
                              Report
                            </Link>
                            <button
                              className="btn-secondary"
                              style={{ padding: '4px 12px', fontSize: 12 }}
                              onClick={() => editingId === c.id ? setEditingId(null) : openEdit(c)}
                            >
                              {editingId === c.id ? 'Close' : 'Edit'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingId === c.id && (
                        <tr>
                          <td colSpan={8} style={{ background: 'var(--bg-secondary)', padding: '16px 20px' }}>
                            {editError && <div className="error-message" style={{ marginBottom: 12, fontSize: 13 }}>{editError}</div>}
                            <form onSubmit={handleSave}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                                <div className="form-group">
                                  <label className="form-label">Name *</label>
                                  <input
                                    className="form-input"
                                    required
                                    value={editForm.name}
                                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Address</label>
                                  <input
                                    className="form-input"
                                    value={editForm.address}
                                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Contact Name</label>
                                  <input
                                    className="form-input"
                                    value={editForm.contactName}
                                    onChange={e => setEditForm(f => ({ ...f, contactName: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Contact Email</label>
                                  <input
                                    className="form-input"
                                    type="email"
                                    value={editForm.contactEmail}
                                    onChange={e => setEditForm(f => ({ ...f, contactEmail: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Contact Phone</label>
                                  <input
                                    className="form-input"
                                    value={editForm.contactPhone}
                                    onChange={e => setEditForm(f => ({ ...f, contactPhone: e.target.value }))}
                                  />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 10 }}>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                  {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => setEditingId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          {clients.length} client{clients.length !== 1 ? 's' : ''}
        </div>
      </div>
    </Layout>
  );
}
